import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useOrg } from "@/hooks/useOrg";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const FONT_OPTIONS: { label: string; value: string }[] = [
  { label: "Sans-serif", value: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif" },
  { label: "Serif", value: "Georgia, 'Times New Roman', Times, serif" },
  { label: "Monospace", value: "'SF Mono', SFMono-Regular, Menlo, Consolas, 'Courier New', monospace" },
  { label: "Rounded", value: "system-ui, 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" },
];

interface BannerData {
  heading: string;
  body: string;
  cta_text: string;
  cta_link: string;
  font: string;
  bg_color: string;
  text_color: string;
  btn_color: string;
  btn_text_color: string;
  btn_border_radius: number;
  padding_x: number;
}

const defaults: BannerData = {
  heading: "",
  body: "",
  cta_text: "",
  cta_link: "",
  font: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  bg_color: "#1a1a1a",
  text_color: "#ffffff",
  btn_color: "#ffffff",
  btn_text_color: "#000000",
  btn_border_radius: 6,
  padding_x: 16,
};

export default function BannerEditor() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const { orgId } = useOrg();
  const [data, setData] = useState<BannerData>(defaults);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgId) return;

    if (!isNew && id) {
      supabase
        .from("banners")
        .select("*")
        .eq("id", id)
        .single()
        .then(({ data: banner, error }) => {
          if (error || !banner) {
            toast.error("Banner not found");
            navigate("/dashboard");
            return;
          }
          setData({
            heading: banner.heading,
            body: banner.body,
            cta_text: banner.cta_text,
            cta_link: banner.cta_link,
            font: banner.font,
            bg_color: banner.bg_color,
            text_color: banner.text_color,
            btn_color: banner.btn_color,
            btn_text_color: banner.btn_text_color,
            btn_border_radius: banner.btn_border_radius,
            padding_x: banner.padding_x,
          });
          setLoading(false);
        });
    } else {
      // Load org branding defaults for new banners
      supabase
        .from("org_branding")
        .select("*")
        .eq("org_id", orgId)
        .maybeSingle()
        .then(({ data: branding }) => {
          if (branding) {
            setData((prev) => ({
              ...prev,
              font: branding.font,
              bg_color: branding.bg_color,
              text_color: branding.text_color,
              btn_color: branding.btn_color,
              btn_text_color: branding.btn_text_color,
              btn_border_radius: branding.btn_border_radius,
              padding_x: branding.padding_x,
            }));
          }
          setLoading(false);
        });
    }
  }, [id, isNew, navigate, orgId]);

  const update = (field: keyof BannerData, value: string | number) => {
    setData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!orgId) return;
    if (data.cta_link && !/^https?:\/\//i.test(data.cta_link)) {
      toast.error("CTA link must start with http:// or https://");
      return;
    }
    setSaving(true);

    if (isNew) {
      const { error } = await supabase.from("banners").insert({ ...data, org_id: orgId });
      if (error) toast.error(error.message);
      else {
        toast.success("Banner created");
        navigate("/dashboard");
      }
    } else {
      const { error } = await supabase.from("banners").update(data).eq("id", id!);
      if (error) toast.error(error.message);
      else toast.success("Banner saved");
    }
    setSaving(false);
  };

  if (loading) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;

  const fontFamily = data.font;

  return (
    <div className="flex h-full flex-col">
      {/* Live preview */}
      <div
        className="flex flex-wrap items-center justify-between gap-2 py-3"
        style={{ backgroundColor: data.bg_color, fontFamily, paddingLeft: `${data.padding_x}px`, paddingRight: `${data.padding_x}px` }}
      >
        {data.body && (
          <span className="text-sm leading-snug" style={{ color: data.text_color }}>
            {data.body}
          </span>
        )}
        {data.cta_text && (
          <a
            href={data.cta_link || "#"}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex shrink-0 items-center px-3 py-1 text-xs font-medium transition-opacity hover:opacity-90"
            style={{
              backgroundColor: data.btn_color,
              color: data.btn_text_color,
              borderRadius: `${data.btn_border_radius}px`,
            }}
            onClick={(e) => e.preventDefault()}
          >
            {data.cta_text}
          </a>
        )}
      </div>

      {/* Editor */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-8">
        <div className="mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} className="gap-1.5 text-muted-foreground">
            <ArrowLeft className="h-3.5 w-3.5" />
            Back
          </Button>
        </div>

        <div className="mx-auto max-w-lg space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              {isNew ? "New banner" : "Edit banner"}
            </h2>
            <p className="text-sm text-muted-foreground">Configure your banner content and style</p>
          </div>

          <section className="space-y-4">
            <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Content</h3>
            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea value={data.body} onChange={(e) => update("body", e.target.value)} placeholder="We just launched something exciting…" rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Button text</Label>
                <Input value={data.cta_text} onChange={(e) => update("cta_text", e.target.value)} placeholder="Learn more" />
              </div>
              <div className="space-y-2">
                <Label>Button link</Label>
                <Input value={data.cta_link} onChange={(e) => update("cta_link", e.target.value)} placeholder="https://…" />
              </div>
            </div>
          </section>

          {/* Styling */}
          <section className="space-y-4">
            <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Style</h3>

            <div className="space-y-2">
              <Label>Font</Label>
              <Select value={data.font} onValueChange={(v) => update("font", v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FONT_OPTIONS.map((f) => (
                    <SelectItem key={f.label} value={f.value}>
                      <span style={{ fontFamily: f.value }}>{f.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <ColorField label="Background" value={data.bg_color} onChange={(v) => update("bg_color", v)} />
              <ColorField label="Text" value={data.text_color} onChange={(v) => update("text_color", v)} />
              <ColorField label="Button" value={data.btn_color} onChange={(v) => update("btn_color", v)} />
              <ColorField label="Button text" value={data.btn_text_color} onChange={(v) => update("btn_text_color", v)} />
            </div>

            <div className="space-y-2">
              <Label>Button border radius: {data.btn_border_radius}px</Label>
              <Slider
                value={[data.btn_border_radius]}
                onValueChange={([v]) => update("btn_border_radius", v)}
                min={0}
                max={24}
                step={1}
              />
            </div>

            <div className="space-y-2">
              <Label>Horizontal padding: {data.padding_x}px</Label>
              <Slider
                value={[data.padding_x]}
                onValueChange={([v]) => update("padding_x", v)}
                min={0}
                max={64}
                step={4}
              />
            </div>
          </section>

          <div className="flex gap-3 pt-4">
            <Button onClick={handleSave} disabled={saving} className="flex-1">
              {saving ? "Saving…" : isNew ? "Create banner" : "Save changes"}
            </Button>
            <Button variant="outline" onClick={() => navigate("/dashboard")}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ColorField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-9 cursor-pointer rounded border border-border bg-transparent p-0.5"
        />
        <Input value={value} onChange={(e) => onChange(e.target.value)} className="font-mono text-xs" />
      </div>
    </div>
  );
}
